import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../ui/status-badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Package, 
  Calendar,
  Clock,
  CreditCard,
  CheckCircle,
  ShoppingCart,
  Bell,
  ChevronDown,
  Info,
  Star,
  TrendingUp,
  Smartphone,
  Users,
  Home,
  Building,
  Activity
} from 'lucide-react';
import { AdminMobileUserAPI, EnhancedMobileUser, UserOrderHistoryResponse, UserCart } from '../../lib/api';
import { toast } from '../ui/use-toast';
import { formatCurrency, formatDateShort, formatTime } from '../../lib/utils';

interface MobileUserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: EnhancedMobileUser;
}

const MobileUserDetailModal: React.FC<MobileUserDetailModalProps> = ({ isOpen, onClose, user }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [orderHistory, setOrderHistory] = useState<UserOrderHistoryResponse | null>(null);
  const [userCart, setUserCart] = useState<UserCart | null>(null);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isLoadingCart, setIsLoadingCart] = useState(false);
  const [taxInfo, setTaxInfo] = useState<Awaited<ReturnType<typeof AdminMobileUserAPI.getUserTaxInfo>>['data'] | null>(null);

  // Reset active tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
    }
  }, [isOpen]);

  // Fetch masked tax identity when the modal opens.
  useEffect(() => {
    if (!isOpen || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await AdminMobileUserAPI.getUserTaxInfo(user._id);
        if (!cancelled && res.success) setTaxInfo(res.data);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, user]);

  // Fetch order history when orders tab is selected
  useEffect(() => {
    if (activeTab === 'orders' && user && !orderHistory) {
      fetchOrderHistory();
    }
  }, [activeTab, user]);

  // Fetch cart when cart tab is selected
  useEffect(() => {
    if (activeTab === 'cart' && user && !userCart) {
      fetchUserCart();
    }
  }, [activeTab, user]);

  const fetchOrderHistory = async () => {
    try {
      setIsLoadingOrders(true);
      const response = await AdminMobileUserAPI.getUserOrderHistory(user._id, {
        page: 1,
        limit: 10
      });
      
      if (response.success) {
        setOrderHistory(response.data);
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch order history",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching order history:", error);
      toast({
        title: "Error",
        description: "Failed to fetch order history",
        variant: "destructive"
      });
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const fetchUserCart = async () => {
    try {
      setIsLoadingCart(true);
      const response = await AdminMobileUserAPI.getUserCart(user._id);
      
      if (response.success) {
        setUserCart(response.data);
      } else {
      }
    } catch (error) {
      console.error("Error fetching user cart:", error);
    } finally {
      setIsLoadingCart(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'delivered':
      case 'completed':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Turn raw status enums (in_transit) into readable labels (In Transit).
  const formatStatus = (status: string) =>
    (status || '')
      .split('_')
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {user.profilePicture ? (
                <img 
                  src={user.profilePicture} 
                  alt={user.firstName}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
              )}
              <div>
                <DialogTitle className="text-2xl font-bold">
                  {user.firstName} {user.lastName || ''}
                </DialogTitle>
                <DialogDescription>
                  Mobile User Details and Activity
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={user.isActive ? 'active' : 'inactive'} />
              <StatusBadge status={user.isPhoneVerified ? 'verified' : 'unverified'} />
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Orders ({user.orderSummary?.totalOrders || 0})
            </TabsTrigger>
            <TabsTrigger value="cart" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Cart ({user.cartSummary?.itemCount || 0})
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Activity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Basic Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">First Name:</span>
                      <div className="font-medium">{user.firstName}</div>
                    </div>
                    {user.lastName && (
                      <div>
                        <span className="text-muted-foreground">Last Name:</span>
                        <div className="font-medium">{user.lastName}</div>
                      </div>
                    )}
                    {user.email && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Email:</span>
                        <div className="font-medium flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {user.email}
                        </div>
                      </div>
                    )}
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Phone:</span>
                      <div className="font-medium flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        {user.phoneNumber || user.phone}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Registered:</span>
                      <div className="font-medium">{formatDateShort(user.createdAt)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last Login:</span>
                      <div className="font-medium">
                        {user.engagementMetrics?.lastLoginAt ? formatDateShort(user.engagementMetrics.lastLoginAt) : 'Never'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Order Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-primary" />
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center p-3 bg-blue-50 rounded-md">
                      <div className="text-2xl font-bold text-blue-600">{user.orderSummary?.totalOrders || 0}</div>
                      <div className="text-muted-foreground">Total Orders</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-md">
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(user.orderSummary?.totalSpent || 0)}
                      </div>
                      <div className="text-muted-foreground">Total Spent</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-md">
                      <div className="text-2xl font-bold text-purple-600">
                        {formatCurrency(user.orderSummary?.averageOrderValue || 0)}
                      </div>
                      <div className="text-muted-foreground">Avg Order Value</div>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-md">
                      <div className="text-2xl font-bold text-orange-600">{user.orderSummary?.pendingOrders || 0}</div>
                      <div className="text-muted-foreground">Pending Orders</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Addresses - Collapsible */}
            {user.addresses && user.addresses.length > 0 && (
              <Collapsible>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-blue-600" />
                          Saved Addresses ({user.addresses.length})
                        </div>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-4">
                        {user.addresses.map((address, index) => (
                          <div key={address._id} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Home className="h-4 w-4" />
                                  <span className="font-medium">{address.name}</span>
                                  <Badge variant="outline" className="capitalize">
                                    {address.type}
                                  </Badge>
                                  {address.isDefault && (
                                    <Badge variant="default">Default</Badge>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  <div>{address.street}</div>
                                  <div>{address.city}, {address.state} - {address.postalCode}</div>
                                  <div>{address.country}</div>
                                  <div className="flex items-center gap-1 mt-1">
                                    <Phone className="h-3 w-3" />
                                    {address.phoneNumber}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Admin Notes - Collapsible */}
            {user.adminNotes && (
              <Collapsible>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Info className="h-5 w-5 text-gray-600" />
                          Admin Notes
                        </div>
                        <ChevronDown className="h-4 w-4" />
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="bg-gray-50 p-4 rounded-md">
                        <p className="text-sm">{user.adminNotes}</p>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Tax identity (masked) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tax Information</CardTitle>
              </CardHeader>
              <CardContent>
                {!taxInfo ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : !taxInfo.hasData ? (
                  <p className="text-sm text-gray-500">No tax information on file.</p>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">PAN</span>
                      <span className="font-medium">
                        {taxInfo.pan?.masked || '—'}
                        {taxInfo.pan && (
                          <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${taxInfo.pan.verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {taxInfo.pan.verified ? 'Verified' : 'Unverified'}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">GSTIN</span>
                      <span className="font-medium">
                        {taxInfo.gstin?.masked || '—'}
                        {taxInfo.gstin && (
                          <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${taxInfo.gstin.verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {taxInfo.gstin.verified ? 'Verified' : 'Unverified'}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Consent</span>
                      <span className="font-medium">
                        {taxInfo.consent?.given ? 'Given' : 'Not given'}
                        {taxInfo.consent?.at ? ` · ${new Date(taxInfo.consent.at).toLocaleDateString()}` : ''}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Order History</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingOrders ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="text-muted-foreground mt-2">Loading orders...</p>
                  </div>
                ) : orderHistory && orderHistory.orders && orderHistory.orders.length > 0 ? (
                  <div className="space-y-4">
                    {orderHistory.orders.map((order) => (
                      <div key={order._id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="font-medium">{order.orderNumber}</div>
                            <div className="text-sm text-muted-foreground">
                              {formatDateShort(order.createdAt)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-lg">{formatCurrency(order.totalAmount)}</div>
                            <StatusBadge status={order.status} />
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <div>Items: {order.items.length}</div>
                          {order.supplierName && <div>Supplier: {order.supplierName}</div>}
                          <div>Delivery: {order.deliveryAddress.city}, {order.deliveryAddress.state}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No orders found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cart" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Current Cart</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingCart ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="text-muted-foreground mt-2">Loading cart...</p>
                  </div>
                ) : userCart && userCart.items && userCart.items.length > 0 ? (
                  <div className="space-y-4">
                    {userCart.items.map((item) => (
                      <div key={item._id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {item.product.images && item.product.images.length > 0 && (
                              <img 
                                src={item.product.images[0]} 
                                alt={item.product.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            )}
                            <div>
                              <div className="font-medium">{item.product.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {item.quantity} {item.unit} × {formatCurrency(item.pricePerUnit)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{formatCurrency(item.totalPrice)}</div>
                            <div className="text-sm text-muted-foreground">
                              Added: {formatDateShort(item.addedAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center text-lg font-bold">
                        <span>Total: {formatCurrency(userCart.summary.totalAmount)}</span>
                        <span>{userCart.summary.itemCount} items</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Cart is empty</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  Engagement Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-3 bg-blue-50 rounded-md">
                    <div className="text-2xl font-bold text-blue-600">
                      {user.engagementMetrics?.totalLogins || 0}
                    </div>
                    <div className="text-muted-foreground">Total Logins</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-md">
                    <div className="text-2xl font-bold text-green-600">
                      {Math.round((user.engagementMetrics?.averageSessionDuration || 0) / 60)}m
                    </div>
                    <div className="text-muted-foreground">Avg Session</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-md">
                    <div className="text-2xl font-bold text-purple-600">
                      {user.cartSummary?.itemCount || 0}
                    </div>
                    <div className="text-muted-foreground">Cart Items</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-md">
                    <div className="text-2xl font-bold text-orange-600">
                      {formatCurrency(user.cartSummary?.totalValue || 0)}
                    </div>
                    <div className="text-muted-foreground">Cart Value</div>
                  </div>
                </div>

                {user.engagementMetrics?.notificationPreferences && (
                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Notification Preferences</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4" />
                        <span className="text-sm">Order Updates</span>
                        <Badge variant={user.engagementMetrics.notificationPreferences.orderUpdates ? 'default' : 'outline'}>
                          {user.engagementMetrics.notificationPreferences.orderUpdates ? 'On' : 'Off'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4" />
                        <span className="text-sm">Promotions</span>
                        <Badge variant={user.engagementMetrics.notificationPreferences.promotions ? 'default' : 'outline'}>
                          {user.engagementMetrics.notificationPreferences.promotions ? 'On' : 'Off'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Smartphone className="h-4 w-4" />
                        <span className="text-sm">App Updates</span>
                        <Badge variant={user.engagementMetrics.notificationPreferences.appUpdates ? 'default' : 'outline'}>
                          {user.engagementMetrics.notificationPreferences.appUpdates ? 'On' : 'Off'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default MobileUserDetailModal;